import React, { FC } from 'react';
import { GenInternalLink } from '@/components/generics';
import {
  AddressIcon,
  CreditCardIcon,
  DeliveredIcon,
  PackageIcon,
  ScheduledIcon,
  GiftIcon,
} from '@/components/ui/Icons';
import { formatDateForDisplay } from '@/utils/dates/dateFormatters';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';
import { trackClickEvent, TypeScheduledItemGroup } from '../../utils';
import {
  buildQueryString,
  constructLegacyCustomerPortalLink,
  SUBSCRIPTION_SOURCE_UUID_KEY,
} from '@/utils/shopSubdomain';
import { DisplayDateFormat } from '@/utils/dates';
import classNames from 'classnames';
import { CTA_NAMES } from '@/utils/rudderstack';

interface OrderInsightsProps {
  t: TypePlayKitPageTranslations;
  group: TypeScheduledItemGroup;
  locale: string;
  showOrderNumber?: boolean;
  showOrderDate?: boolean;
  showShippingAddress?: boolean;
  showEditShippingAddress?: boolean;
  showNextPaymentDate?: boolean;
  showPrepaidDate?: boolean;
  showGiftActivationDate?: boolean;
}

interface InfoRowProps {
  icon: React.ComponentType;
  label: string;
  value?: string;
  children?: React.ReactNode;
  shouldLineBreakValueDisplayInMobile?: boolean;
  shouldHideOverflow?: boolean;
}

const InfoRow = ({
  icon: Icon,
  label,
  value,
  children,
  shouldLineBreakValueDisplayInMobile,
  shouldHideOverflow,
}: InfoRowProps) => (
  <div
    className={classNames('flex flex-row gap-1.5', {
      'items-center': !shouldLineBreakValueDisplayInMobile,
      'items-start md:items-center': shouldLineBreakValueDisplayInMobile,
    })}
  >
    <Icon />
    <div
      className={classNames('flex items-center', {
        'flex-col md:flex-row md:gap-1.5': shouldLineBreakValueDisplayInMobile,
        'flex-row gap-1.5': !shouldLineBreakValueDisplayInMobile,
      })}
    >
      <p className="w-max font-bold">{label}</p>
      {value && (
        <p
          title={value}
          className={classNames({
            'md:max-w-full md:text-wrap max-w-28 text-ellipsis overflow-hidden text-nowrap':
              shouldHideOverflow,
          })}
        >
          {value}
        </p>
      )}
    </div>
    {children}
  </div>
);

const OrderInsights: FC<OrderInsightsProps> = ({
  t,
  group,
  locale,
  showOrderDate = false,
  showOrderNumber = false,
  showShippingAddress = false,
  showEditShippingAddress = false,
  showNextPaymentDate = false,
  showPrepaidDate = false,
  showGiftActivationDate = false,
}) => {
  const { order, primaryPaymentMethodLocalizedLabel, subscriptionUuid } = group;

  const OrderNumber = () => (
    <InfoRow icon={PackageIcon} label={t.orderNumber}>
      <GenInternalLink
        href={`${order?.status_url}?language=${locale}`}
        cta={order?.playgenius_order_id || ''}
      />
    </InfoRow>
  );

  const OrderDate = () => (
    <InfoRow
      icon={DeliveredIcon}
      label={t.orderDate}
      value={formatDateForDisplay(
        order?.created_at || '',
        locale,
        DisplayDateFormat.ShortMonthAndDay,
      )}
    ></InfoRow>
  );

  const GiftActivationDate = () => (
    <InfoRow
      icon={GiftIcon}
      label={t.giftActivationDate}
      value={formatDateForDisplay(
        order?.created_at || '',
        locale,
        DisplayDateFormat.ShortMonthAndDay,
      )}
    ></InfoRow>
  );

  const queryParameters = [
    {
      key: SUBSCRIPTION_SOURCE_UUID_KEY,
      value: subscriptionUuid,
    },
  ];
  const queryString = buildQueryString(queryParameters);
  const editShipAddressLink = constructLegacyCustomerPortalLink(
    locale,
    [
      group.userUuid,
      'account-settings',
      'edit-shipping-address',
      order?.shipping_address.original_uuid || '',
    ],
    queryString,
  );

  const managePaymentMethodLink = constructLegacyCustomerPortalLink(
    locale,
    [group.userUuid, 'account-settings', 'payment-method'],
    queryString,
  );

  const OrderShippingAddress = () => (
    <InfoRow
      icon={AddressIcon}
      label={t.shippingAddress}
      value={order?.shipping_address.line1}
      shouldHideOverflow
    >
      {showEditShippingAddress && (
        <GenInternalLink
          href={editShipAddressLink}
          cta={t.edit}
          onClick={() => {
            trackClickEvent(CTA_NAMES.EDIT_SHIPPING_ADDRESS);
          }}
        />
      )}
    </InfoRow>
  );

  const NextPaymentDate = () => {
    const targetScheduledItem = group.items[0];
    const scheduledDateYear = new Date(
      targetScheduledItem.scheduled_date,
    ).getFullYear();
    const currentYear = new Date().getFullYear();
    const isDateWithinCurrentYear = scheduledDateYear === currentYear;
    const paymentDate = targetScheduledItem.scheduled_date
      ? formatDateForDisplay(
          targetScheduledItem.scheduled_date,
          locale,
          isDateWithinCurrentYear
            ? DisplayDateFormat.ShortMonthAndDay
            : DisplayDateFormat.ShortMonthDayYear,
        )
      : undefined;

    return (
      <InfoRow
        icon={ScheduledIcon}
        label={t.nextPaymentDate}
        value={paymentDate}
      />
    );
  };

  const PaymentMethod = () => (
    <InfoRow
      icon={CreditCardIcon}
      label={t.paymentMethodLabel}
      value={primaryPaymentMethodLocalizedLabel}
      shouldLineBreakValueDisplayInMobile
    >
      <GenInternalLink
        href={managePaymentMethodLink}
        cta={t.manage}
        onClick={() => {
          trackClickEvent(CTA_NAMES.MANAGE_PAYMENT_METHOD);
        }}
      />
    </InfoRow>
  );

  const PrepaidDate = () => (
    <InfoRow
      icon={ScheduledIcon}
      label={t.prepaidDate}
      value={formatDateForDisplay(
        order?.created_at || '',
        locale,
        DisplayDateFormat.ShortMonthAndDay,
      )}
    />
  );

  return (
    <div className="flex flex-col gap-1.5 pb-6">
      {showPrepaidDate && <PrepaidDate />}
      {showNextPaymentDate && <NextPaymentDate />}
      {primaryPaymentMethodLocalizedLabel && <PaymentMethod />}
      {showOrderDate && <OrderDate />}
      {showGiftActivationDate && <GiftActivationDate />}
      {showShippingAddress && <OrderShippingAddress />}
      {showOrderNumber && <OrderNumber />}
    </div>
  );
};

export default OrderInsights;
